import { useNotification } from '@/components/general/NotificationProvider';
import Modal from '@/components/general/Modal';
import { unshareFolder } from '@/api';
import CloseIcon from '@/assets/close.svg';

interface RemoveSharingProps {
  show: boolean;
  folderId: number;
  folderName: string;
  sharer: string;
  onClose: () => void;
  onRefresh: () => void;
}

export function RemoveSharing({ show, folderId, folderName, sharer, onClose, onRefresh }: RemoveSharingProps) {
  const { showNotification } = useNotification();

  const handleUnshare = async () => {
    try {
      const result = await unshareFolder(folderId);

      if (result.succeeded) {
        onRefresh();
        onClose();
      } else {
        showNotification('Server call to remove folder sharing failed!', 'error');
      }
    } catch {
      showNotification('Failed to unshare folder', 'error');
    }
  };

  return (
    <Modal open={show} onClose={onClose} contanerClassName="w-full max-w-lg bg-white">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="px-6 pt-4 pb-2.5 text-lg font-medium text-gray-900">
          Unshare Folder <strong className="font-bold">{folderName}</strong>
        </h2>
        <button onClick={onClose} className="mr-5 cursor-pointer rounded-2xl p-1 hover:bg-gray-100">
          <CloseIcon className="h-7 w-7 text-gray-600" />
        </button>
      </div>

      {/* Content */}
      <div className="px-6">
        <div className="mb-4 text-sm text-black/60">
          This folder was shared with you by <span className="font-medium">{sharer}</span>
        </div>
        <div className="space-y-1 text-sm text-black/60">
          <div>Are you sure you wish to remove yourself from this shared folder?</div>
          <div>This action can not be undone.</div>
          <div className="mt-2 text-sm text-black/60">
            To restore access you&apos;ll have to ask <span className="font-medium">{sharer}</span> to reshare the
            folder with you.
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between rounded-b-sm bg-gray-50 p-4">
        <div></div>
        <div className="flex space-x-3">
          <button
            onClick={handleUnshare}
            className="cursor-pointer rounded-full bg-black px-6 py-3 text-sm font-medium text-white transition-colors"
          >
            Ok
          </button>
          <button
            onClick={onClose}
            className="cursor-pointer rounded-full bg-gray-100 px-6 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-300 hover:text-gray-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
